<!-- src/components/AppHeader.vue -->
<template>
    <header class="app-header" :class="{ bg_img: hasBackgroundImage }" >
        <div class="header_row">
            <div class="brand">
                <router-link :to="{ name: 'home', params: { lang: currentLanguage } }">
                    <img :src="logo" />
                </router-link>
                <button class="mobile-menu-toggle" @click="toggleMobileMenu">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                        <path fill="currentColor" d="M3 4h18v2H3V4zm0 7h18v2H3v-2zm0 7h18v2H3v-2z"/>
                    </svg>
                </button>
            </div>
            <nav class="main-nav" :class="{ 'mobile-open': isMobileMenuOpen }">
                <router-link 
                    :to="{ name: 'home', params: { lang: currentLanguage } }" 
                    class="nav-item" 
                    :class="{ active: isHomeActive }"
                    @click="closeMobileMenu">
                    {{ $t('home') }}
                </router-link>
                <router-link 
                    :to="{ name: 'cars', params: { lang: currentLanguage } }" 
                    class="nav-item" 
                    :class="{ active: isCarsActive }"
                    @click="closeMobileMenu">
                    {{ $t('all_vehicles') }}
                </router-link>
                <router-link 
                    :to="{ name: 'about', params: { lang: currentLanguage } }" 
                    class="nav-item"
                    @click="closeMobileMenu">
                    {{ $t('about_us') }}
                </router-link>
                <router-link 
                    :to="{ name: 'contact', params: { lang: currentLanguage } }" 
                    class="nav-item"
                    @click="closeMobileMenu">
                    {{ $t('contact_nav') }}
                </router-link>
                <router-link 
                    :to="{ name: 'sell_car', params: { lang: currentLanguage } }"
                    class="nav-item"
                    @click="closeMobileMenu">
                    {{ $t('sell_car') }}
                </router-link>

                <div class="language-switcher">
                    <button 
                        class="language-toggle"
                        @click="toggleLanguageDropdown"
                        :title="currentLanguage.toUpperCase()">
                        <img :src="getFlag(currentLanguage)" :alt="currentLanguage + ' flag'">
                    </button>
                    <div class="language-dropdown" v-show="showLanguageDropdown">
                        <button 
                            v-for="lang in availableLanguages" 
                            :key="lang"
                            @click="setLanguage(lang)"
                            :class="{ active: currentLanguage === lang }"
                            :title="lang.toUpperCase()">
                            <img :src="getFlag(lang)" :alt="lang + ' flag'">
                        </button>
                    </div>
                </div>
            </nav>
        </div>
    </header>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

const { locale } = useI18n()
const route = useRoute()
const router = useRouter()
const logo = ref(require('@/assets/images/logowhite2.png'))
const fr_flag = ref(require('@/assets/images/flags/fr.svg'))
const nl_flag = ref(require('@/assets/images/flags/nl.svg'))
const en_flag = ref(require('@/assets/images/flags/en.svg'))
const showLanguageDropdown = ref(false)
const isMobileMenuOpen = ref(false)

const availableLanguages = ['nl', 'fr', 'en']
const currentLanguage = computed(() => route.params.lang || 'nl')

const getFlag = (lang) => {
    switch(lang) {
        case 'fr': return fr_flag.value
        case 'nl': return nl_flag.value
        case 'en': return en_flag.value
        default: return nl_flag.value
    }
}

const hasBackgroundImage = computed(() => {
    return route.name !== 'home'
})

const isHomeActive = computed(() => {
    return route.name === 'home'
})

const isCarsActive = computed(() => {
    return route.name === 'cars' || route.name === 'car-detail'
})

const toggleLanguageDropdown = () => {
    showLanguageDropdown.value = !showLanguageDropdown.value
}

const toggleMobileMenu = () => {
    isMobileMenuOpen.value = !isMobileMenuOpen.value
    showLanguageDropdown.value = false
}

const closeMobileMenu = () => {
    isMobileMenuOpen.value = false
}

const setLanguage = (lang) => {
    locale.value = lang
    showLanguageDropdown.value = false
    router.push({
        name: route.name,
        params: { ...route.params, lang },
        query: route.query
    })
}

const handleClickOutside = (event) => {
    const languageSwitcher = document.querySelector('.language-switcher')
    if (languageSwitcher && !languageSwitcher.contains(event.target)) {
        showLanguageDropdown.value = false
    }
    
    const header = document.querySelector('.app-header')
    if (header && !header.contains(event.target)) {
        isMobileMenuOpen.value = false
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            isMobileMenuOpen.value = false
            showLanguageDropdown.value = false
        }
    })
})

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
})

watch(
    () => route.params.lang,
    (newLang) => {
        if (newLang) {
            locale.value = newLang
        }
    },
    { immediate: true }
)
</script>

<style scoped>
    .app-header {
        padding: 1rem 2rem;
        width: 100%;
        background-color: #222; 
        color: #fff; 
        box-shadow: 0 20px 20px #0000003d;
        position: fixed;
        top: 0;
        z-index: 100;
    }

    .header_row {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .bg_img {
        /* background: url('../assets/images/hero_img.jpg') no-repeat; */
        background-size: cover;
    }

    .header-container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 2rem;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .brand {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
    }

    .brand img {
        width: 300px;
/*        max-width: 100%;*/
    }

    .mobile-menu-toggle {
        background: none;
        border: none;
        color: white;
        cursor: pointer;
        display: none;
        padding: 0.5rem;
    }

    .main-nav {
        display: flex;
        align-items: center;
        gap: 2rem;
        width: 100%;
        justify-content: flex-end;
    }

    .nav-item {
        color: white;
        text-decoration: none;
        font-weight: 500;
        padding: 0.5rem 0;
        position: relative;
        font-size: 18px;
    }

    .nav-item:hover {
        color: #45ABFF;
    }

    .nav-item.active {
        color: #45ABFF;
    }

    .nav-item.active::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 2px;
        background-color: #45ABFF;
    }

    .language-switcher {
        position: relative;
        margin-left: 1rem;
    }

    .language-toggle {
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
        width: 25px;
        height: 25px;
        overflow: hidden;
        position: relative;
        transition: transform 0.2s;
    }

    .language-toggle:hover {
        transform: scale(1.1);
    }

    .language-toggle img {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    .language-dropdown {
        position: absolute;
        top: 100%;
        right: 0;
        background: white;
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        padding: 0.5rem;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        z-index: 101;
        margin-top: 0.5rem;
    }

    .language-dropdown button {
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
        width: 25px;
        height: 25px;
        overflow: hidden;
        transition: transform 0.2s;
    }

    .language-dropdown button:hover {
        transform: scale(1.1);
    }

    .language-dropdown button img {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    @media (max-width: 768px) {
        .app-header {
            padding: 1rem;
        }
        
        .mobile-menu-toggle {
            display: block;
        }
        
        .main-nav {
            position: fixed;
            top: 80px;
            left: 0;
            width: 100%;
            background-color: #222;
            flex-direction: column;
            gap: 1rem;
            padding: 1rem 0 2rem;
            align-items: center;
            transform: translateY(-150%);
            transition: transform 0.3s ease-in-out;
            z-index: 99;
        }
        
        .main-nav.mobile-open {
            transform: translateY(0);
            box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
        }
        
        .nav-item {
            padding: 0.75rem 0;
            font-size: 1.1rem;
        }
        
        .language-switcher {
            margin: 0.5rem 0 0;
        }
        
        .language-dropdown {
            right: auto;
            left: 50%;
            transform: translateX(-50%);
        }

        .brand img {
            width: 200px;
        }
    }

    @media (min-width: 769px) {
        .main-nav {
            display: flex !important;
        }
    }
</style>